// Canal entre la interfaz (React) y el mundo 3D. El mundo lee este estado en cada
// cuadro; React lo escribe cuando cambia la ruta, el scroll o la posición de los
// textos. Así la escena no depende de React y React no espera a la escena.

/**
 * Escena que muestra el mundo (guía 7.4). Cada página elige la suya; el mundo es uno
 * solo y la cámara viaja de una a otra (guía 7.5).
 * - home: el guion del inicio, controlado por el scroll.
 * - research: el árbol de cerca (Investigación).
 * - models: los tres árboles en un claro de neblina (Modelos).
 * - audit: el cielo de modelos, explorable (Auditoría).
 * - still: la ciudad en plano contemplativo (Empresa y Equipo).
 * - lost: el árbol chico que duda (404).
 * - market: el súper de noche (Productos): la vidriera, Yvyra o Tajy.
 * - background: el mundo lejano y quieto (el resto).
 */
export type WorldMode = "home" | "research" | "models" | "audit" | "still" | "lost" | "market" | "background";
export type WorldStatus = "idle" | "loading" | "ready" | "fallback";

/** Rectángulo de un bloque de texto en píxeles CSS, con cuánto hay que oscurecer detrás. */
export type TextRect = { x: number; y: number; w: number; h: number; shade: number };

/** Persona vista por la cámara de Tajy: caja y huesos en píxeles CSS. */
export type PersonScreen = { box: [number, number, number, number]; bones: number[]; fallen: boolean; visible: boolean };

/** Lo que la interfaz dibuja encima del súper (el mundo lo escribe en cada cuadro). */
export type MarketScreen = {
  /** Esquinas de la pantalla del celular (arriba izq., arriba der., abajo der., abajo izq.). */
  phone: number[] | null;
  /** De dónde sale la consulta (la yerba de 1 kg). */
  query: { x: number; y: number } | null;
  people: PersonScreen[];
  gaps: { x: number; y: number; w: number; h: number }[];
  /** Estado de la escena (story.ts): realce de la yerba, conversación, caída, vista de Tajy. */
  focus: number;
  chat: number;
  fall: number;
  cctv: number;
};

/** Posición en pantalla de cada familia del cielo (para la información al pasar). */
export type FamilyScreen = { x: number; y: number; r: number; visible: boolean };

type Listener = () => void;

class WorldBus {
  mode: WorldMode = "background";
  /** Posición en el guion del inicio (0 → 1 plano 1, 1 → 2 plano 2...). */
  u = 0;
  pointer = { x: 0, y: 0 };
  readonly rects = new Map<string, TextRect>();
  /**
   * Posición de la palabra «YvyrAI» del HTML (borde izquierdo, borde superior de su caja
   * de interlineado 1 y tamaño de fuente, en píxeles CSS con el escenario fijado): la
   * escena dibuja la suya exactamente encima, con las mismas métricas de la fuente.
   */
  wordBox: { left: number; top: number; fontSize: number } | null = null;
  /** Posición que la cámara está mostrando de verdad (después del resorte). */
  uRendered = 0;
  /** Vuelta de deliberación que muestra la escena (para el contador visible). */
  loopCount = 0;
  status: WorldStatus = "idle";
  /** Por qué el mundo pasó al respaldo (shader, contexto perdido, cuadro negro...). */
  brokenReason = "";
  /** Informe de diagnóstico de la GPU (se muestra con ?diag en la URL). */
  diag = "";
  /** Se incrementa con cada navegación interna: el mundo sabe que hubo un cambio. */
  navigation = 0;

  // ---- Investigación ----
  /** Vueltas elegidas en el control de 1 a 16 (la escena hace esas vueltas). */
  treeLoops = 7;
  /** Se incrementa para pedir que el árbol repita las vueltas. */
  treeReplay = 0;
  /** Zona del árbol iluminada por el paso de "Cómo piensa" (−1: ninguna, 0 a 3). */
  treeZone = -1;

  // ---- Modelos ----
  /** Árbol resaltado al pasar o tocar (−1: ninguno; 0 Nano, 1 150M, 2 1B). */
  modelsFocus = -1;

  // ---- Productos ----
  /** Qué plano del súper muestra Productos y un contador para repetir la escena. */
  market = { view: "front" as "front" | "yvyra" | "tajy", replay: 0 };
  /** Lo escribe el mundo en cada cuadro cerca del súper (inicio y Productos). */
  marketScreen: MarketScreen = { phone: null, query: null, people: [], gaps: [], focus: 0, chat: 0, fall: 0, cctv: 0 };

  // ---- Auditoría ----
  audit = {
    /** Órbita libre: giro, inclinación y distancia (la página los mueve con arrastre). */
    yaw: 0.35,
    pitch: 0.12,
    zoom: 1,
    /** true mientras dura el vuelo de "Encontrar el nuestro". */
    findOurs: false,
    /** Visibilidad por familia (índices de src/world/data/families.ts) y por región. */
    familyMask: [] as number[],
    regionMask: [1, 1, 1] as [number, number, number],
    /** Familia resaltada (−1: ninguna). */
    focus: -1,
  };
  /** Lo escribe el mundo en cada cuadro del modo audit (y en el cielo del inicio). */
  familyScreen: FamilyScreen[] = [];
  /** Posición en pantalla del punto nuestro (null si está fuera de cuadro). */
  oursScreen: { x: number; y: number } | null = null;

  // ---- Easter eggs y vida ----
  /** Árbol florecido (7 clics en el logo): 0 → 1 y vuelve. */
  bloom = 0;
  /** Paleta Midnight City. */
  midnight = false;
  /** Clima real de Asunción: 0 seco, 1 llovizna, 2 lluvia, 3 tormenta. */
  rain = 0;
  /** Hora de Asunción en horas decimales (la escena cambia sutilmente con ella). */
  hour = 23;
  /** Salvapantallas: la cámara recorre la ciudad despacio. */
  idle = false;
  /** La escena se apaga (sudo rm -rf /): 0 encendida, 1 apagada. */
  blackout = 0;

  private listeners = new Set<Listener>();

  subscribe(fn: Listener) {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  private emit() {
    this.listeners.forEach((fn) => fn());
  }

  setStatus(status: WorldStatus) {
    if (this.status === status) return;
    this.status = status;
    if (typeof document !== "undefined") document.documentElement.dataset.world = status;
    this.emit();
  }

  setMode(mode: WorldMode) {
    if (mode === this.mode) return;
    this.mode = mode;
    this.navigation++;
  }

  setRect(id: string, rect: TextRect | null) {
    if (rect) this.rects.set(id, rect);
    else this.rects.delete(id);
  }
}

export const worldBus = new WorldBus();

/** Escena de cada ruta (guía 7.4). Lo que no figura usa el mundo de fondo. */
export function modeForPath(path: string): WorldMode {
  if (path === "/") return "home";
  if (path === "/investigacion") return "research";
  if (path === "/modelos") return "models";
  if (path === "/auditoria") return "audit";
  if (path === "/productos") return "market";
  if (path === "/empresa" || path === "/equipo") return "still";
  return "background";
}
